import { Alert, Box, Button, Grid, Switch, Typography } from '@mui/material';
import { includes, map } from 'es-toolkit/compat';
import { unwoundRowMetadataFromId } from 'interfaces/genericFields/unwindRowsWithInnerArrays';
import { slidesBaseFields } from 'interfaces/procedure/fields/slideFields';
import React from 'react';

import {
  DataGridInterfaceWrapper,
  DataGridInterfaceWrapperProps,
} from '../ProceduresPage/DataGrids/DataGridInterfaceWrapper';

const manifestFieldKeys = [
  'label',
  'slideId',
  'originalFileName',
  'stainingType',
  'cancerTypeId',
  'cancerSubtypes',
  'biopsySiteId',
  'biopsyType',
  'preservationMethod',
  'procedureType',
  'metastasis',
  'positiveControl',
  'negativeControl',
];
const manifestFieldLabelOverrides = { slideId: 'Slide ID' };

interface CaseManifestPlanTableProps {
  labId: string;
  editableCaseIds: number[];
  editableSlideIds: string[];
  gridProps: DataGridInterfaceWrapperProps;
  hasDraftCases: boolean;
  isCaseLabelEditable: (slideId: string) => boolean;
  onRemoveSelectedSlides: () => void;
  onShowOnlyErrorsChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  removeButtonText: string;
  selectedSlideCount: number;
  showOnlyErrors: boolean;
}

export const CaseManifestPlanTable = ({
  labId,
  editableCaseIds,
  editableSlideIds,
  gridProps,
  hasDraftCases,
  isCaseLabelEditable,
  onRemoveSelectedSlides,
  onShowOnlyErrorsChange,
  removeButtonText,
  selectedSlideCount,
  showOnlyErrors,
}: CaseManifestPlanTableProps) => (
  <Box my={1}>
    <Grid container justifyContent="space-between" alignItems="center" p={0.5} rowGap={1}>
      <Grid item>
        <Grid container alignItems="center" columnGap={2} rowGap={1}>
          <Grid item>
            <Grid container alignItems="center">
              <Grid item>
                <Typography>Show only errors</Typography>
              </Grid>
              <Grid item>
                <Switch
                  checked={showOnlyErrors}
                  size="small"
                  onChange={onShowOnlyErrorsChange}
                  inputProps={{ 'aria-label': 'Show only errors' }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {hasDraftCases && (
        <Grid item>
          <Button
            variant="outlined"
            color="primary"
            onClick={onRemoveSelectedSlides}
            disabled={selectedSlideCount === 0}
          >
            {removeButtonText}
          </Button>
        </Grid>
      )}
    </Grid>
    <Alert severity="info" sx={{ mb: 1 }}>
      Double-click an editable cell to change its value. To update multiple rows at once, select the rows, then click
      Bulk Edit.
    </Alert>
    {showOnlyErrors && gridProps.totalSlides === 0 && (
      <Typography role="status" sx={{ mb: 1 }}>
        No case-plan rows have errors
      </Typography>
    )}
    <DataGridInterfaceWrapper
      {...gridProps}
      additionalSlideColumns={[
        ...(gridProps.additionalSlideColumns || []),
        {
          field: 'sourceSlideUri',
          headerName: 'Source path',
          width: 300,
          sortable: false,
          editable: false,
          renderCell: ({ row }) => (
            <Typography variant="body2">
              {row.labId === labId && row.sourceSlideUri ? row.sourceSlideUri : 'Unavailable'}
            </Typography>
          ),
        },
      ]}
      columnOrder={['targetCaseAssignment', 'label', 'slideId', 'originalFileName', 'sourceSlideUri']}
      forceHeight={650}
      includedFieldKeys={manifestFieldKeys}
      fieldLabelOverrides={manifestFieldLabelOverrides}
      disableEditing={false}
      pendingSlidesMode={false}
      hideActionsMenu
      dataGridProps={{
        isCellEditable: (params) => {
          const { baseRowId, innerRowId: slideId } = unwoundRowMetadataFromId(params.row.id);
          const isSlideField = includes(map(slidesBaseFields, 'dataKey'), params.field);
          const isEditableManifestRow = includes(editableSlideIds, slideId);
          const isEditableManifestCaseField = includes(editableCaseIds, Number(baseRowId));
          if (!isEditableManifestRow || params.field === 'originalFileName') return false;
          if (params.field === 'label' && !isCaseLabelEditable(String(slideId))) return false;
          return isSlideField || isEditableManifestCaseField;
        },
      }}
      hideLinksToViewer
      hideCaseIdColumn
    />
  </Box>
);
